import dayjs from 'dayjs';
import { clsx } from 'clsx';
import moment from 'moment-timezone';
import { RcFile } from 'antd/lib/upload';
import localeData from 'dayjs/plugin/localeData';
import { Button, DatePicker, Select } from 'antd';
import React, { Dispatch, SetStateAction, useEffect, useState } from 'react';

import { showAxiosError } from '@utils';
import {
  Admin, AdminOptionCats,
  Crop, DealerDocumentProps,
  dealerPaymentType,
  dealerTiers,
  DealerType,
  State,
} from '@types';
import { UploadIcon } from '@components/Crops';
import { financeSettingsHttp } from '@network/finance-settings-http';
import { convertFileToBase64, FileUpload, InputText, SelectedPhoneInput, SwitchInput } from '@components';
import {
  adminHttp, adminSettingsHttp,
  cropHttp,
  productInvoiceHttp,
  stateHttp,
  UserChannel,
  userHttp,
} from '@network';
import { MinusOutlined } from '@ant-design/icons';
import { DealerDocuments } from '@pages/dealer/components/DealerDocuments';
import { InputSelect, InputSelectAutocomplete } from '@components/inputs/InputSelect';
import { SwitcherControllerInput } from '@components/inputs/SwitcherControlledInput';
import cls from './buyer-form.module.css';


dayjs.extend(localeData);

const initialCrop = {
  crop: '', ibm: '',
};
const MONTHS = ['Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio', 'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre', 'Todo el año'];
const MAX_IMAGE_SIZE = 5;
const MAX_VALUE = 10000;
const docsTitle = {
  [DealerType.BUYER]: 'Off - Taker Documents',
  [DealerType.RETAILER]: 'Retailer Documents',
  [DealerType.ASSOCIATE]: 'Associate Documents',
};

interface CreatorFormProps {
  formValues: any;
  setFormValues: any;
  showErrors: boolean;
  dealerType: DealerType;
  documents: DealerDocumentProps[];
  setDocuments: Dispatch<SetStateAction<DealerDocumentProps[]>>;
  loading: boolean;
}

interface Option {
  value: number;
  name: string;
}

export const BuyerForm = ({
  formValues, setFormValues, showErrors, dealerType, documents, setDocuments, loading,
}: CreatorFormProps) => {
  const [crops, setCrops] = useState<Crop[]>([]);
  const [states, setStates] = useState<State[]>([]);
  const [categories, setCategories] = useState<any[]>([]);
  const [brands, setBrands] = useState<any[]>([]);
  const [sources, setSources] = useState<UserChannel[]>([]);
  const [activations, setActivations] = useState<any[]>([]);
  const [owners, setOwners] = useState<Admin[]>([]);
  const [statuses, setStatuses] = useState<Option[]>([]);
  const [types, setTypes] = useState<Option[]>([]);

  const showNameError = (formValues.name || '').length === 0 && showErrors;
  const showTier = dealerType === DealerType.BUYER;
  const showWebsite = [DealerType.BUYER, DealerType.RETAILER].includes(dealerType);
  const showChannel =
    [DealerType.BUYER, DealerType.RETAILER, DealerType.ASSOCIATE].includes(dealerType);
  const showSize = [DealerType.BUYER].includes(dealerType);
  const typesCategory = {
    [DealerType.BUYER]: AdminOptionCats.PARTNERS_TYPE_BUYER,
    [DealerType.RETAILER]: AdminOptionCats.PARTNERS_TYPE_RETAILER,
    [DealerType.ASSOCIATE]: AdminOptionCats.PARTNERS_TYPE_ASSOCIATE,
  } as const;
  const showActivation = true;
  const showTierError = showTier && (formValues.tier || '').length === 0 && showErrors;

  useEffect(() => {
    (async () => {
      const [
        allCrops,
        allStates,
        allSources,
        allActivations,
        allCategories,
        allBrands,
        admins,
        statuses,
        types,
      ] = await Promise.all([
        cropHttp.getAll(),
        stateHttp.getAll(),
        userHttp.getSources(),
        financeSettingsHttp.getActivations(),
        productInvoiceHttp.getCategories(),
        productInvoiceHttp.getBrands(),
        adminHttp.getResponsibleOwners(),
        adminSettingsHttp.getOptionsByCategory(AdminOptionCats.PARTNERS_STATUS),
        adminSettingsHttp.getOptionsByCategory(typesCategory[dealerType]),
      ]);

      setCrops(allCrops);
      setStates(allStates);
      setSources(allSources);
      setActivations(allActivations);
      setCategories(allCategories.map(i => i.name));
      setBrands(allBrands.map(i => i.name));
      setOwners(admins);
      setStatuses(statuses.map(i => ({
        value: i.id as number,
        name: i.name,
      })));
      setTypes(types.map(i => ({
        value: i.id as number,
        name: i.name,
      })));
    })();
  }, []);

  const onChangeHandler = (e: any) => {
    setFormValues({
      ...formValues,
      [e.target.name]: e.target.value,
    });
  };

  const onChangePhoneHandler = (phone: string) => {
    setFormValues({ ...formValues, phone });
  };

  const addCropsIbmHandler = () => {
    if (hasEmptyCrops) {
      return;
    }

    const currentCropIbms = formValues.cropsIbm || [];

    setFormValues(prev => ({
      ...prev,
      cropsIbm: [...currentCropIbms, initialCrop],
    }));
  };

  const removeCropsIbmHandler = (index: number) => {
    setFormValues(prev => ({
      ...prev,
      cropsIbm: prev.cropsIbm.filter((_, i) => i !== index),
    }));
  };

  const updateCropsIbmHandler = (index: number, value: any) => {
    const newCrops = [...formValues.cropsIbm];
    newCrops[index] = { ...newCrops[index], crop: value };

    setFormValues(prev => ({
      ...prev,
      cropsIbm: newCrops,
    }));
  };

  const updateUniqueCropsIBMHandler = (index: number, value: any) => {
    const newCrops = [...formValues.cropsIbm];
    newCrops[index] = { ...newCrops[index], ibm: value };

    setFormValues(prev => ({
      ...prev,
      cropsIbm: newCrops,
    }));
  };


  const onChangeSelectHandler = (e: any, name: string, list: {id: any; name: string}[]) => {
    setFormValues({
      ...formValues,
      [name]: list.find(i => e.target.value === i.name)?.id || null,
    });
  };

  const onChangeNumberHandler = (
    e: any, withMax = true, float = false,
  ) => {
    const input = e.target.value;
    let value: string;
    if (float) {
      let cleaned = input.replace(/[^0-9.]/g, '');
      let parts = cleaned.split('.');

      if (parts.length > 2) {
        cleaned = parts[0] + '.' + parts.slice(1).join('');
        parts = cleaned.split('.');
      }

      if (parts[1]) {
        parts[1] = parts[1].slice(0, 2);
      }

      value = parts.join('.');
    } else {
      value = input.replace(/\D/g, '');
    }
    value = withMax && +value > MAX_VALUE ? MAX_VALUE.toString() : value;
    setFormValues({
      ...formValues,
      [e.target.name]: value,
    });
  };

  const onChangeDateHandler = (date: any, name: string) => {
    setFormValues({
      ...formValues,
      [name]: date,
    });
  };
  const onChangeActivation = (activationId: number) => {
    setFormValues({ ...formValues, activationId });
  };

  const onChangeOwner = (ownerId: number) => {
    setFormValues({ ...formValues, ownerId });
  };

  const onChangeStatus = (statusId: number) => {
    setFormValues({ ...formValues, statusId });
  };

  const onChangeType = (priorityId: number) => {
    setFormValues({ ...formValues, priorityId });
  };

  const onCompanyChange = (isCompany: boolean) => {
    console.log(isCompany);
    setFormValues({ ...formValues, isCompany });
  };

  const onImageClear = () => {
    setFormValues({
      ...formValues,
      image: '',
    });
  };

  const uploadPicture = async (rcFile: RcFile): Promise<any> => {
    try {
      const base64Img = await convertFileToBase64(rcFile) as string;

      if (base64Img) {
        setFormValues({
          ...formValues, image: base64Img as string,
        });
      }
    } catch (err: any) {
      showAxiosError(err);
    }
  };
  const maxLength = 66;

  const UploadContent = () => {
    return (
      <div className={cls.uploadContent}>
        <div className={cls.uploadContentBlock}>
          <div className={cls.uploadContentTitle}>Upload Image</div>
          <div className={cls.uploadContentIcon}>
            <UploadIcon />
          </div>
        </div>
        <div className={cls.uploadContentComment}>
          *File size should not exceed {MAX_IMAGE_SIZE}MB
        </div>
      </div>
    );
  };

  const priorityOptions = dealerType === DealerType.BUYER
    ? ['AAA', 'Comercializador', 'Exportador']
    : dealerType === DealerType.RETAILER
      ? ['Fertilizantes y agroquímicos', 'Semillas', 'Estructura',
        'Maquinaria', 'Fabricante de Insumos']
      : ['Broker Financiero'];

  const hasEmptyCrops = (formValues.cropsIbm || []).some(({ crop }) => crop === '');

  useEffect(() => {
    if (formValues.cropsIbm?.length === 0) {
      addCropsIbmHandler();
    }
  }, []);

  return (
    <div className={cls.form}>
      <div className={cls.block}>
        <div className={clsx(cls.col, cls.col1, cls[dealerType])}>
          <div className={clsx(cls.row)}>
            <div className={cls.floatField}>
              <div className={cls.floatFieldRow}>
                <div className={cls.floatFieldCol}>
                  Partner Type:
                </div>
                <div className={cls.floatFieldCol}>
                  <SwitcherControllerInput
                    defaultValue={formValues.isCompany || true}
                    onChange={onCompanyChange}
                    labelLeft="PFAE"
                    labelRight="PM"
                    className={cls.floatSwitcher}
                    labelClassName={cls.floatSwitcherLabel}
                  />
                </div>
              </div>
            </div>
          </div>

          <div className={clsx(cls.row, cls.rowName)}>
            <InputText
              name="name"
              label="Name*"
              value={formValues.name || ''}
              onChange={onChangeHandler}
              className={clsx(showNameError && cls.error)}
              required
              maxLength={maxLength}
            />
          </div>

          <div className={clsx(cls.row, cls.rowPhone)}>
            <SelectedPhoneInput
              value={formValues.phone || ''}
              onChange={onChangePhoneHandler}
              placeholder="(000) 000-0000"
              className={cls.phoneInput}
              label="Phone"
              labelWidth={125}
              name="phone"
            />
          </div>

          <div className={clsx(cls.row, cls.rowEmail)}>
            <InputText
              name="email"
              label="E-mail"
              value={formValues.email || ''}
              onChange={onChangeHandler}
              maxLength={maxLength}
            />
          </div>

          {showTier && (
            <div className={clsx(cls.row, cls.rowTier)}>
              <InputSelect
                name="tier"
                label="Buyer Tier*"
                onChange={onChangeHandler}
                values={dealerTiers}
                selectedValue={formValues.tier || ''}
                className={clsx(showTierError && cls.error)}
              />
            </div>
          )}

          {showActivation && (
            <div className={clsx(cls.row, cls.rowActivation)}>
              <InputSelectAutocomplete
                wordWrap
                label="Activation"
                onChange={onChangeActivation}
                options={activations.map(activation => ({
                  value: activation.id,
                  label: activation.activation,
                }))}
                selectedValue={formValues.activationId || ''}
                displayEmpty
              />
            </div>
          )}

          <div className={clsx(cls.row, cls.rowStatus)}>
            <InputSelectAutocomplete
              label="Status"
              onChange={onChangeStatus}
              options={statuses.map(item => ({
                value: item.value,
                label: item.name,
              }))}
              selectedValue={formValues.statusId || ''}
              displayEmpty
            />
          </div>

          <div className={clsx(cls.row, cls.rowOwner)}>
            <InputSelectAutocomplete
              label="Owner"
              onChange={onChangeOwner}
              options={owners.map(item => ({
                value: item.id,
                label: item.name,
              }))}
              selectedValue={formValues.ownerId || ''}
              displayEmpty
            />
          </div>

          {(dealerType === DealerType.RETAILER) && (
            <div className={clsx(cls.row, cls.rowStoreCount)}>
              <InputText
                name="storeCount"
                label="Store Count"
                value={formValues.storeCount || ''}
                onChange={onChangeNumberHandler}
              />
            </div>
          )}

          {(dealerType === DealerType.BUYER) && (
            <div className={clsx(cls.row, cls.rowCiec)}>
              <InputText
                name="ciec"
                label="CIEC"
                value={formValues.ciec || ''}
                onChange={onChangeHandler}
                onInput={transformCiecInput}
                maxLength={8}
              />
            </div>
          )}

          <div className={clsx(cls.row, cls.rowStatus)}>
            <InputSelectAutocomplete
              label="Type"
              onChange={onChangeType}
              options={types.map(item => ({
                value: item.value,
                label: item.name,
              }))}
              selectedValue={formValues.priorityId || ''}
              displayEmpty
            />
          </div>

          {(dealerType === DealerType.BUYER) && (
            <div className={clsx(cls.row, cls.rowSaleScope)}>
              <InputSelect
                selectedValue={formValues.saleScope}
                name="saleScope"
                label="Sale Scope"
                onChange={onChangeHandler}
                values={['National', 'International', 'Both']}
                displayEmpty
              />
            </div>
          )}

          {(dealerType === DealerType.BUYER || dealerType === DealerType.RETAILER) && (
            <div className={clsx(cls.row, cls.rowYearsExp)}>
              <InputText
                name="yearsExp"
                label="Years Exp"
                value={formValues.yearsExp || ''}
                onChange={onChangeNumberHandler}
              />
            </div>
          )}

          {(dealerType === DealerType.RETAILER) && (
            <div className={clsx(cls.row, cls.rowClientLoan)}>
              <InputSelect
                selectedValue={formValues.clientLoan}
                name="clientLoan"
                label="Client Loan"
                onChange={onChangeHandler}
                values={['Yes', 'No']}
                displayEmpty
              />
            </div>
          )}
        </div>

        <div className={clsx(cls.col, cls.col2, cls[dealerType])}>
          <div className={clsx(cls.row, cls.rowStates)}></div>
          <div className={clsx(cls.row, cls.rowStates)}>
            <InputSelect
              selectedValue={formValues.states || []}
              wordWrap
              name="states"
              label={'States'}
              onChange={onChangeHandler}
              values={states.map(state => state.name)}
              multiple
            />
          </div>

          {showSize && (
            <div className={clsx(cls.row, cls.rowSize)}>
              <InputText
                name="size"
                label="Size"
                value={formValues.size || ''}
                onChange={onChangeNumberHandler}
                postfix="Ha"
              />
            </div>
          )}

          <div className={clsx(cls.row, cls.rowFarmerCount)}>
            <InputText
              name="farmerCount"
              label="Farmer Count"
              value={formValues.farmerCount || ''}
              onChange={onChangeNumberHandler}
              postfix={formValues.farmerCount > 1 ? 'farmers' : 'farmer'}
            />
          </div>

          {showChannel && (
            <div className={clsx(cls.row, cls.rowSource)}>
              <InputSelect
                selectedValue={formValues.source || ''}
                name="source"
                label="Channel"
                onChange={onChangeHandler}
                values={sources.map(source => source.name)}
                displayEmpty
              />
            </div>
          )}

          {(dealerType === DealerType.BUYER) && (
            <div className={clsx(cls.row, cls.rowFinancialVol)}>
              <InputText
                name="financialVol"
                label="Financial Vol"
                value={formValues.financialVol?.toString() || ''}
                onChange={e => onChangeNumberHandler(e, false, true)}
                prefix={formValues.financialVol ? '$' : ''}
              />
            </div>
          )}

          {showWebsite && (
            <div className={clsx(cls.row, cls.rowWebsite)}>
              <InputText
                name="website"
                label="Website"
                value={formValues.website || ''}
                onChange={onChangeHandler}
              />
            </div>
          )}

          {(dealerType === DealerType.RETAILER) && (
            <div className={clsx(cls.row, cls.rowCategories)}>
              <InputSelect
                selectedValue={formValues.pCategories || []}
                wordWrap
                name="pCategories"
                label="P.Categories"
                onChange={onChangeHandler}
                values={categories}
                multiple
              />
            </div>
          )}

          {(dealerType === DealerType.RETAILER) && (
            <div className={clsx(cls.row, cls.rowBrands)}>
              <InputSelect
                selectedValue={formValues.pBrands || []}
                wordWrap
                name="pBrands"
                label="P.Brands"
                onChange={onChangeHandler}
                values={brands}
                multiple
              />
            </div>
          )}

          {(dealerType === DealerType.BUYER) && (
            <div className={clsx(cls.row, cls.rowSupplierLoan)}>
              <InputSelect
                selectedValue={formValues.supplierLoan}
                name="supplierLoan"
                label="Supplier Loan"
                onChange={onChangeHandler}
                values={['Yes', 'No']}
                displayEmpty
              />
            </div>
          )}

          <div className={clsx(cls.row, cls.rowCloseDate)}>
            <div className={cls.date}>
              <div className={cls.label}>Close Date</div>
              <div className={cls.dateInput}>
                <DatePicker
                  name="closeDate"
                  className={cls.input}
                  placeholder="DD / MM / YYYY"
                  value={formValues.closeDate ? moment(formValues.closeDate) : undefined}
                  onChange={e => onChangeDateHandler(e, 'closeDate')}
                />
              </div>
            </div>
          </div>

          {(dealerType === DealerType.BUYER) && (
            <div className={clsx(cls.row, cls.rowHarvestDate)}>
              <div className={cls.date}>
                <div className={cls.label}>Harvest Date</div>
                <div className={cls.dateInput}>
                  <DatePicker
                    name="harvestDate"
                    className={cls.input}
                    placeholder="DD / MM / YYYY"
                    value={formValues.harvestDate ? moment(formValues.harvestDate) : undefined}
                    onChange={e => onChangeDateHandler(e, 'harvestDate')}
                  />
                </div>
              </div>
            </div>
          )}

          {(dealerType === DealerType.BUYER) && (
            <div className={clsx(cls.row, cls.rowSowingDate)}>
              <div className={cls.date}>
                <div className={cls.label}>Sowing Date</div>
                <div className={cls.dateInput}>
                  <DatePicker
                    name="sowingDate"
                    className={cls.input}
                    placeholder="DD / MM / YYYY"
                    value={formValues.sowingDate ? moment(formValues.sowingDate) : undefined}
                    onChange={e => onChangeDateHandler(e, 'sowingDate')}
                  />
                </div>
              </div>
            </div>
          )}

          {(dealerType === DealerType.BUYER || dealerType === DealerType.RETAILER) && (
            <div className={clsx(cls.row, cls.rowPaymentType)}>
              <InputSelect
                selectedValue={dealerPaymentType.find(i => formValues.paymentType === i.id)?.name || ''}
                name="paymentType"
                label="Payment Type"
                onChange={e => onChangeSelectHandler(e, 'paymentType', dealerPaymentType)}
                values={dealerPaymentType.map(i => i.name)}
                displayEmpty
              />
            </div>
          )}

          {(dealerType === DealerType.ASSOCIATE) && (
            <div className={clsx(cls.row, cls.rowLoanType)}>
              <InputSelect
                selectedValue={formValues.loanType}
                name="loanType"
                label="Loan Type"
                onChange={onChangeHandler}
                values={['Simple', 'Revolvente', 'Refaccionario', 'Avío']}
                displayEmpty
              />
            </div>
          )}

          {(dealerType === DealerType.RETAILER || dealerType === DealerType.ASSOCIATE) && (
            <div className={clsx(cls.row, cls.rowMktStrategy)}>
              <InputSelect
                selectedValue={formValues.mktStrategy}
                name="mktStrategy"
                label="Mkt Strategy"
                onChange={onChangeHandler}
                values={['Traditional', 'Digital', 'Offline', 'Events', 'Other']}
                displayEmpty
              />
            </div>
          )}

          {(dealerType === DealerType.ASSOCIATE) && (
            <div className={clsx(cls.row, cls.rowAssYearsExp)}>
              <InputText
                name="yearsExp"
                label="Years Exp"
                value={formValues.yearsExp || ''}
                onChange={onChangeNumberHandler}
              />
            </div>
          )}
        </div>
      </div>

      <div className={cls.crops}>
        <div className={cls.cropsTitle}>Unique Crops</div>
        <div className={cls.cropsTable}>
          <div className={cls.cropsTableHead}>
            <div className={cls.cropsTableCol}>Crop name</div>
            <div className={cls.cropsTableCol}>IBM</div>
          </div>
          <div>
            {(formValues.cropsIbm || []).map((cropIbm, index) => (
              <div key={`crop-${index}`} className={cls.cropsTableRow}>
                <div className={cls.cropsTableRemove} onClick={() => removeCropsIbmHandler(index)}>
                  <MinusOutlined />
                </div>
                <Select
                  className={cls.cropsTableCol}
                  onChange={(value) => updateCropsIbmHandler(index, value)}
                  options={crops
                    .filter(crop => formValues.cropsIbm.every(i => i.crop !== crop.nameMx))
                    .map(crop => ({
                      label: crop.nameMx,
                      value: crop.nameMx,
                    }))}
                  value={cropIbm.crop}
                />
                <Select
                  className={cls.cropsTableCol}
                  onChange={(value) => updateUniqueCropsIBMHandler(index, value)}
                  options={MONTHS.map(ibm => ({
                    label: ibm,
                    value: ibm,
                  }))}
                  mode="multiple"
                  value={cropIbm.ibm || []}
                  maxTagCount={3}
                />
              </div>
            ))}
          </div>
          <div className={cls.cropsTableActions}>
            <Button
              className={cls.cropsTableAdd}
              htmlType="button"
              onClick={addCropsIbmHandler}
              disabled={hasEmptyCrops}
            >+</Button>
          </div>
        </div>
      </div>

      <div className={cls.documentsBlock}>
        <div className={cls.documentsTitle}>Documents</div>
        <div className={cls.documentsRow}>
          <div className={clsx(cls.documentsCol, cls.mainImg)}>
            {formValues.image?.length ? (
              <div className={cls.uploadedBox} onClick={onImageClear}>
                <div className={cls.uploadedIcon}>
                  <UploadIcon />
                </div>
                <img src={formValues.image} alt="" className={cls.image} />
              </div>
            ) : (
              <FileUpload
                uploadAction={uploadPicture}
                className={cls.uploadBox}
                type="image"
                content={<UploadContent />}
                size={MAX_IMAGE_SIZE}
              />
            )}
          </div>
          <div className={clsx(cls.documentsCol, cls.otherDocs)}>
            <div className={cls.docsTitle}>{dealerType ? docsTitle[dealerType] : 'Documents'}</div>
            <DealerDocuments
              loading={loading}
              documents={documents}
              setDocuments={setDocuments}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

const transformCiecInput = (e: any) => {
  if (e.target && e.target.value) {
    e.target.value = e.target.value.replace(/[^0-9A-Za-z]/g, '');
  }
};
